import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { RootState } from 'store';
import {
  clearMembers,
  getStorageUnitsResponse,
  setDirectAccess,
} from 'store/storageunits/reducer';
import type { GetStorageUnitsRequestActionInterface } from 'store/storageunits/sagaActions';
import {
  getAccessInformationForStorageUnitRequest,
  getRecentRequest,
  getStorageUnitsRequest,
} from 'store/storageunits/sagaActions';
import { LIMIT } from 'config/paginationConfig';
import {
  ExplorerMode,
  Filter,
  SortType,
  StorageUnitType,
} from 'store/storageunits/types';
import { useIsProject } from 'hooks/useIsProject';
import partition from 'util/partition';
import {
  getHasMoreDataToRetrieve,
  getStorageUnitsForDashboard,
  getRecentFiles,
} from 'store/storageunits/selectors';
import { Box, Stack } from '@mui/material';
import FileArea from '../components/FileArea';
import StorageUnitGrid, {
  useUnitDisplayLimit,
} from '../components/StorageUnitGrid';
import StorageUnitList from '../components/StorageUnitList';
import { useDashboard, useSelection } from '../context';
import { useFetchViewStorageUnits } from '../hooks/useFetchViewStorageUnits';
import { routes } from 'util/routes';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { hasOfflineFileData } from 'store/storageunits/utils';
import { matchesStorageUnitFilters } from 'store/storageunits/browsingUtils';

const Home = () => {
  const { explorerMode } = useDashboard();
  const { setRecentUnits } = useSelection();
  const recentLimit = useUnitDisplayLimit();

  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const isProject = useIsProject();

  const {
    searchString,
    filters,
    advancedFilters,
    fetchedCount,
    loading,
    resolvedStorageUnitIds,
  } = useSelector((state: RootState) => state.storageUnitsState);
  const resolvedFolderId = id ? resolvedStorageUnitIds[id] : undefined;
  const currentFolderId = resolvedFolderId ?? id;

  const moreDataToRetrieve = useSelector(getHasMoreDataToRetrieve);
  const storageUnits = useSelector(getStorageUnitsForDashboard);
  const recentFiles = useSelector(getRecentFiles);

  /* Fetch ancestors of current folder */
  useEffect(() => {
    if (currentFolderId) {
      /* User is inside a folder */
      dispatch(getStorageUnitsRequest({ descendantId: currentFolderId }));
      dispatch(
        getAccessInformationForStorageUnitRequest(currentFolderId, true),
      );
    } else {
      /* User is in root */
      dispatch(getStorageUnitsResponse({ folderPath: [] }));
      dispatch(clearMembers());
      dispatch(setDirectAccess(true));
    }
  }, [currentFolderId, dispatch]);

  useEffect(() => {
    if (id && resolvedFolderId) {
      void navigate(routes.folder(resolvedFolderId) + search, {
        replace: true,
      });
    }
  }, [id, navigate, resolvedFolderId, search]);

  const fetchMore = useCallback(() => {
    if (searchString !== '') {
      dispatch(
        getStorageUnitsRequest({
          parentId: currentFolderId,
          searchString,
          limit: LIMIT,
          offset: fetchedCount,
        }),
      );
    } else {
      dispatch(
        getStorageUnitsRequest({
          parentId: currentFolderId,
          limit: LIMIT,
          offset: fetchedCount,
          replace: false,
          type: StorageUnitType.File,
        }),
      );
    }
  }, [currentFolderId, dispatch, fetchedCount, searchString]);

  useEffect(() => {
    const filtersActive =
      filters.length > 0 ||
      Boolean(advancedFilters.country) ||
      Boolean(advancedFilters.application);
    if (
      filtersActive &&
      !loading &&
      moreDataToRetrieve &&
      fetchedCount > 0 &&
      storageUnits.length < 30
    ) {
      fetchMore();
    }
  }, [
    advancedFilters.application,
    advancedFilters.country,
    fetchedCount,
    fetchMore,
    filters.length,
    loading,
    moreDataToRetrieve,
    storageUnits.length,
  ]);

  let fetchParams: GetStorageUnitsRequestActionInterface['payload'] = {
    parentId: currentFolderId,
    limit: LIMIT,
    offset: 0,
  };

  if (searchString !== '') {
    fetchParams = { ...fetchParams, searchString };
  }

  useFetchViewStorageUnits(getStorageUnitsRequest, fetchParams);

  const downloadedActive = filters.includes(Filter.DOWNLOADED);
  const showRecent =
    currentFolderId === undefined &&
    (downloadedActive ||
      (filters.length === 0 &&
        !advancedFilters.country &&
        !advancedFilters.application &&
        searchString === ''));

  useEffect(() => {
    if (showRecent) {
      dispatch(getRecentRequest());
    }
  }, [dispatch, showRecent]);

  const [downloadedRecentIds, setDownloadedRecentIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!downloadedActive) {
      return;
    }
    let mounted = true;
    void Promise.all(
      recentFiles.map(async (recent) => {
        try {
          const cached = await storageUnitCache.getOne(recent.id);
          return hasOfflineFileData(cached) ? recent.id : null;
        } catch {
          return null;
        }
      }),
    ).then((ids) => {
      if (mounted) {
        setDownloadedRecentIds(
          new Set(ids.filter((id): id is string => id !== null)),
        );
      }
    });
    return () => {
      mounted = false;
    };
  }, [downloadedActive, recentFiles]);

  const visibleRecentFiles = useMemo(
    () =>
      downloadedActive
        ? recentFiles.filter(
            (recent) =>
              downloadedRecentIds.has(recent.id) &&
              matchesStorageUnitFilters(recent, filters, advancedFilters),
          )
        : recentFiles,
    [
      advancedFilters,
      downloadedActive,
      downloadedRecentIds,
      filters,
      recentFiles,
    ],
  );

  const displayedRecentFiles = useMemo(
    () =>
      explorerMode === ExplorerMode.Grid && showRecent
        ? visibleRecentFiles.slice(0, recentLimit)
        : [],
    [explorerMode, recentLimit, showRecent, visibleRecentFiles],
  );

  useEffect(() => {
    setRecentUnits(displayedRecentFiles);
  }, [displayedRecentFiles, setRecentUnits]);

  useEffect(() => () => setRecentUnits([]), [setRecentUnits]);

  const [files, foldersAndProjects] = partition(
    storageUnits,
    (u) => u.type === StorageUnitType.File,
  );

  const [projects, folders] = partition(foldersAndProjects, isProject);

  const showSorterOnFolders = folders.length > 0;
  const showSorterOnProjects = !showSorterOnFolders && projects.length > 0;
  return (
    <FileArea
      dataLength={files.length}
      canFetchMore={moreDataToRetrieve}
      fetchMore={fetchMore}
    >
      {explorerMode === ExplorerMode.Grid ? (
        <Stack spacing={2}>
          {showRecent && (
            <Box
              sx={{
                pb: 2,
              }}
            >
              <StorageUnitGrid
                title='Recent files'
                units={displayedRecentFiles}
                type={StorageUnitType.File}
                noWrap
              />
            </Box>
          )}
          <StorageUnitGrid
            title='Folders'
            units={folders}
            type={StorageUnitType.Folder}
            showSorter={showSorterOnFolders}
          />
          <StorageUnitGrid
            title='Projects'
            units={projects}
            type={StorageUnitType.Folder}
            showSorter={showSorterOnProjects}
          />
          <StorageUnitGrid
            title='Files'
            units={files}
            type={StorageUnitType.File}
            showSorter={!showSorterOnFolders && !showSorterOnProjects}
            sx={{ pb: 10 }}
          />
        </Stack>
      ) : (
        <StorageUnitList
          units={[...folders, ...projects, ...files]}
          columns={[
            { label: 'Name', type: SortType.ALPHABETICAL },
            { label: 'Access' },
            {
              label: 'Last modified',
              type: SortType.LAST_MODIFIED,
            },
            { label: 'Locked' },
            { label: 'Downloaded' },
            { label: 'State' },
          ]}
        />
      )}
    </FileArea>
  );
};

export default Home;
